import React from 'react';
import PropTypes from 'prop-types';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  Visibility as ViewIcon,
  ContentCopy as CopyIcon,
  AutoAwesome as AutoAwesomeIcon,
  Description as DescriptionIcon,
  DriveFileMove as MoveIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';

// Frozen module-level singletons for empty default props (#943,
// react/no-object-type-as-default-prop). A `= []` / `= {}` default builds a NEW
// value on every render, so a memoised child or an effect dependency sees a change
// that never happened.
const EMPTY_ARRAY = Object.freeze([]);

/**
 * ContextMenu component for tree view items
 * 
 * This component encapsulates the context menu functionality and its actions.
 * It accepts action handlers as props and provides a consistent UI for context menus.
 */
const ContextMenu = ({
  anchorPosition,
  onClose,
  onAction,
  item,
  disabledActions = EMPTY_ARRAY
}) => {
  // Handle menu item click with proper action type
  const handleMenuItemClick = (actionType) => () => {
    onClose();
    if (onAction && typeof onAction === 'function') {
      // Call onAction with the action type, item ID, and the full item object
      onAction(actionType, item?.id, item);
    }
  };

  // Check if an action should be disabled
  const isActionDisabled = (actionName) => {
    return disabledActions.includes(actionName);
  };
  // Only render if anchorPosition has position data
  if (!anchorPosition) {
    return null;
  }

  // Check if this is a background context menu (no item)
  const isBackground = anchorPosition.isBackground || !item;
  // #287: a right-click on a PROJECT group node gets its own menu — none of the
  // per-item actions below (View/Edit/Link/BulkAI/Delete-as-item) apply to a
  // project, and its "add" actions must land INSIDE this project specifically,
  // not wherever the background/parent-inference logic would otherwise resolve.
  const isProject = !isBackground && item?._kind === 'project';

  if (isProject) {
    return (
      <Menu
        open={Boolean(anchorPosition)}
        onClose={onClose}
        anchorReference="anchorPosition"
        anchorPosition={{ top: anchorPosition.mouseY, left: anchorPosition.mouseX }}
      >
        <MenuItem onClick={handleMenuItemClick('newProject')} disabled={isActionDisabled('newProject')}>
          <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
          <ListItemText>New Project</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuItemClick('copyProject')} disabled={isActionDisabled('copyProject')}>
          <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Copy Project…</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuItemClick('add')} disabled={isActionDisabled('add')}>
          <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
          <ListItemText>New Item</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuItemClick('addContainer')} disabled={isActionDisabled('addContainer')}>
          <ListItemIcon><CreateNewFolderIcon fontSize="small" /></ListItemIcon>
          <ListItemText>New Container</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuItemClick('exportProjectMarkdown')} disabled={isActionDisabled('exportProjectMarkdown')}>
          <ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Export to Markdown…</ListItemText>
        </MenuItem>
        <Divider />
        {/* #195: archive was previously only reachable from Settings → Organisation
            → Projects — not from the tree, despite the issue explicitly asking for
            a right-click entry point. */}
        <MenuItem onClick={handleMenuItemClick('archiveProject')} disabled={isActionDisabled('archiveProject')}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Archive Project…</ListItemText>
        </MenuItem>
      </Menu>
    );
  }

  // #735: the background menu is ONE org-level action. Its old entries
  // ("Create new product" — single-tree-era wording, no product concept exists —
  // and "Create top-level container", T1.6) resolved their target project
  // implicitly and dead-ended on the Z4 fail-shut guard in a multi-project org:
  // AddDialog has no project picker, so the create was rejected with no way
  // forward. Item/container creation lives on the project node's own menu
  // (#287 — New Item / New Container land inside THAT project); the background
  // offers the one action that needs no project target.
  if (isBackground) {
    return (
      <Menu
        open={Boolean(anchorPosition)}
        onClose={onClose}
        anchorReference="anchorPosition"
        anchorPosition={{ top: anchorPosition.mouseY, left: anchorPosition.mouseX }}
      >
        <MenuItem
          onClick={handleMenuItemClick('newProject')}
          disabled={isActionDisabled('newProject')}
          data-testid="context-menu-new-project"
        >
          <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
          <ListItemText>New Project</ListItemText>
        </MenuItem>
      </Menu>
    );
  }

  return (
    <Menu
      open={Boolean(anchorPosition)}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={
        anchorPosition
          ? { top: anchorPosition.mouseY, left: anchorPosition.mouseX }
          : undefined
      }
    >
      <MenuItem
        onClick={handleMenuItemClick('view')}
        disabled={isActionDisabled('view')}
      >
        <ListItemIcon>
          <ViewIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>View Details</ListItemText>
      </MenuItem>

      <MenuItem
        onClick={handleMenuItemClick('edit')}
        disabled={isActionDisabled('edit')}
      >
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Edit</ListItemText>
      </MenuItem>

      <Divider />

      <MenuItem
        onClick={handleMenuItemClick('add')}
        disabled={isActionDisabled('add')}
      >
        <ListItemIcon>
          <AddIcon fontSize="small" />
        </ListItemIcon>        <ListItemText>
          Add Item
        </ListItemText>
      </MenuItem>

      <MenuItem
        onClick={handleMenuItemClick('addContainer')}
        disabled={isActionDisabled('addContainer')}
      >
        <ListItemIcon>
          <CreateNewFolderIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>
          Add Container
        </ListItemText>
      </MenuItem>

      {!isBackground && (
        <>
          <MenuItem
            onClick={handleMenuItemClick('link')}
            disabled={isActionDisabled('link')}
          >
            <ListItemIcon>
              <LinkIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Create Link</ListItemText>
          </MenuItem>

          {/* #408: move an item to a different parent via a tree picker modal. */}
          <MenuItem
            onClick={handleMenuItemClick('move')}
            disabled={isActionDisabled('move')}
          >
            <ListItemIcon>
              <MoveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Move…</ListItemText>
          </MenuItem>

          {/* SC 2.5.7 Dragging Movements, AA (#648): reordering a sibling was
              drag-only (and drag is disabled on mobile entirely), leaving mobile
              users with no way to reorder. These write through the SAME
              sibling_order / PUT-items-move path drag reorder uses (see
              planMoveSibling in RequirementsTreeGrid.js) — reparent already has
              the non-drag "Move…" path above; this only covers reorder. */}
          <MenuItem
            onClick={handleMenuItemClick('moveUp')}
            disabled={isActionDisabled('moveUp')}
            data-testid="context-menu-move-up"
          >
            <ListItemIcon>
              <ArrowUpwardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Move up</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={handleMenuItemClick('moveDown')}
            disabled={isActionDisabled('moveDown')}
            data-testid="context-menu-move-down"
          >
            <ListItemIcon>
              <ArrowDownwardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Move down</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={handleMenuItemClick('bulkAi')}
            disabled={isActionDisabled('bulkAi')}
          >
            <ListItemIcon>
              <AutoAwesomeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Run AI on all items…</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={handleMenuItemClick('exportMarkdown')}
            disabled={isActionDisabled('exportMarkdown')}
          >
            <ListItemIcon>
              <DescriptionIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export to Markdown…</ListItemText>
          </MenuItem>

          <Divider />
          
          <MenuItem 
            onClick={handleMenuItemClick('copy')}
            disabled={isActionDisabled('copy')}
          >
            <ListItemIcon>
              <CopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copy ID</ListItemText>
          </MenuItem>
          
          <MenuItem 
            onClick={handleMenuItemClick('delete')}
            disabled={isActionDisabled('delete')}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
          </MenuItem>
        </>
      )}    </Menu>
  );
};

ContextMenu.propTypes = {
  anchorPosition: PropTypes.shape({
    mouseX: PropTypes.number,
    mouseY: PropTypes.number,
    id: PropTypes.string,
    item: PropTypes.object,
    selectedItems: PropTypes.array,
    isBackground: PropTypes.bool
  }),
  onClose: PropTypes.func.isRequired,
  onAction: PropTypes.func.isRequired,
  item: PropTypes.object,
  disabledActions: PropTypes.arrayOf(PropTypes.string)
};

export default ContextMenu;
